import { RouteComponentProps } from "@reach/router";
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import "./MetaLiftCreate.scss";
import { DateRange } from "../utils/types";
import MetaLiftBestPractices from "./MetaLiftBestPractices";
import MetaLiftForm from "./MetaLiftForm";
import { CustomConversionOption, CustomEventOption } from "./MetaLiftObjectiveCell";
import MetaLiftTestPower from "./MetaLiftTestPower";
import { RunningPowerCheckModal } from "./PowerCheckModals";
import { Dropdown, DropdownToggleType } from "../Components/Dropdown/Dropdown";
import { FullPageSpinner } from "../Components/Spinner";
import {
  getActiveTestType,
  calculateTotalBudget,
  dateTimeToUnixTimestamp,
  TimezoneOption,
  buildLiftTestPayload,
  MultiCellPowerTestResult,
  CellPowerResult,
  computeCellPowerResults,
  buildTotalPowerCalcResults,
  buildCellLevelPowerCalcResults,
  applyRemainderCell,
  applyDefaultSplitsToCells,
} from "./MetaLiftStudyUtils";
import { awaitJSON, MetaLambdaFetch, FetchError } from "../utils/fetch-utils";
import {
  MetaBudgetInfo,
  CampaignCell,
  ObjectiveCell,
} from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";
import { useSelector } from "react-redux";
import * as UserRedux from "../redux/user";
import { useSetError } from "../redux/modals";

export interface AllowedMetaAccount {
  account_id: string;
  account_name: string;
  business_manager: string;
  has_pixel: boolean;
}

interface AccountScopedData {
  campaigns: MetaBudgetInfo[];
  pixelId: string | undefined;
  customConversions: CustomConversionOption[];
}

interface MetaLiftCreateProps {
  company: string;
  onNavigateToPublished?: () => void;
}

export type TestPowerStatus = "not_run" | "well_powered" | "not_well_powered" | "error";

interface TestPowerFormParams {
  campaignCells: CampaignCell[];
  objectiveCells: ObjectiveCell[];
  dateInputs: Partial<DateRange>;
  selectedTestOptions: {
    accounts: boolean;
    campaigns: boolean;
    adSets: boolean;
  };
}

const GENERIC_POWER_ERROR = "Something went wrong calculating power. Please try again.";

type PowerCheckOutcome =
  | { status: "well_powered" | "not_well_powered" }
  | { status: "error"; message: string };

const getPowerCheckErrorMessage = (error: unknown): string => {
  if (error instanceof FetchError && error.status === 422) {
    try {
      const parsed = JSON.parse(error.message);
      if (typeof parsed?.message === "string" && parsed.message) {
        return parsed.message;
      }
    } catch {
      // not JSON-wrapped; fall through to generic
    }
  }
  return GENERIC_POWER_ERROR;
};

export const MetaLiftCreate = ({
  company,
  onNavigateToPublished,
}: RouteComponentProps & MetaLiftCreateProps): JSX.Element => {
  const userEmail = useSelector(UserRedux.emailSelector);
  const setError = useSetError();
  const [testName, setTestName] = useState<string>("");
  const [testDescription, setTestDescription] = useState<string>("");
  const [testHypothesis, setTestHypothesis] = useState<string>("");
  const [plannedActions, setPlannedActions] = useState<string>("");
  const [dateInputs, setDateInputs] = useState<Partial<DateRange>>({
    start: "",
    end: "",
  });
  const [timeZone, setTimeZone] = useState<TimezoneOption>("Pacific");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [selectedTestOptions, setSelectedTestOptions] = useState<{
    accounts: boolean;
    campaigns: boolean;
    adSets: boolean;
  }>({
    accounts: false,
    campaigns: true,
    adSets: false,
  });

  const [campaignCells, setCampaignCells] = useState<CampaignCell[]>([
    {
      id: "1",
      name: "",
      testPercentage: 0.9,
      controlPercentage: 0.1,
      selectedItems: [],
    },
  ]);
  const [nextCellId, setNextCellId] = useState<number>(2);
  const [objectiveCells, setObjectiveCells] = useState<ObjectiveCell[]>([
    { id: "1", name: "", eventNames: [] },
  ]);
  const [nextObjectiveId, setNextObjectiveId] = useState<number>(2);
  const [testPowerStatus, setTestPowerStatus] = useState<TestPowerStatus>("not_run");
  const [lastCheckedParams, setLastCheckedParams] = useState<TestPowerFormParams | null>(null);
  const [campaignOptions, setCampaignOptions] = useState<MetaBudgetInfo[]>();
  const [accounts, setAccounts] = useState<AllowedMetaAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AllowedMetaAccount>();
  const [pixelId, setPixelId] = useState<string>();
  const [customConversions, setCustomConversions] = useState<CustomConversionOption[]>([]);
  const [customEvents, setCustomEvents] = useState<CustomEventOption[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [customEventsLoaded, setCustomEventsLoaded] = useState(false);
  const [accountDataLoaded, setAccountDataLoaded] = useState(false);
  const accountCacheRef = useRef<Map<string, AccountScopedData>>(new Map());
  const isLoading =
    !accountsLoaded ||
    !customEventsLoaded ||
    (Boolean(selectedAccount) && (!campaignsLoaded || !accountDataLoaded));

  const [showRunningModal, setShowRunningModal] = useState<boolean>(false);
  const [powerCheckError, setPowerCheckError] = useState<string | null>(null);
  const [needDailySpend, setNeedDailySpend] = useState<number>(0);
  const [historicalDailySpend, setHistoricalDailySpend] = useState<number>(0);
  const [testLengthDays, setTestLengthDays] = useState<number>(0);
  const [powerTestResult, setPowerTestResult] = useState<MultiCellPowerTestResult | null>(null);
  const [minRequiredDurationDays, setMinRequiredDurationDays] = useState<number>(0);
  const [isCellToCellSensitivityPowered, setIsCellToCellSensitivityPowered] = useState<boolean>(
    false
  );
  const [pairwiseValue, setPairwiseValue] = useState<number>(0);

  const lockedAccountId = useMemo(
    () => (selectedAccount ? `act_${selectedAccount.account_id}` : null),
    [selectedAccount]
  );

  const allSelectedItemIds = useMemo(() => {
    const ids = new Set<string>();
    campaignCells.forEach(cell => {
      cell.selectedItems.forEach(item => ids.add(item.id));
    });
    return ids;
  }, [campaignCells]);

  const totalBudget = useMemo(
    () => calculateTotalBudget(campaignCells, campaignOptions || [], selectedTestOptions),
    [campaignCells, campaignOptions, selectedTestOptions]
  );

  const cellPowerResults = useMemo<CellPowerResult[]>(
    () => (powerTestResult ? computeCellPowerResults(powerTestResult) : []),
    [powerTestResult]
  );

  const startTimeUnix = dateTimeToUnixTimestamp(dateInputs.start, startTime, timeZone);
  const endTimeUnix = dateTimeToUnixTimestamp(dateInputs.end, endTime, timeZone);

  const addCampaignCell = useCallback(() => {
    if (campaignCells.length >= 5) {
      return;
    }

    const newCellNumber = nextCellId;
    const uniqueId = `${newCellNumber}`;

    setNextCellId(prev => prev + 1);
    const newCells = [
      ...campaignCells,
      {
        id: uniqueId,
        name: "",
        testPercentage: 0,
        controlPercentage: 0,
        selectedItems: [],
      },
    ];
    setCampaignCells(applyDefaultSplitsToCells(newCells));
  }, [campaignCells, nextCellId]);

  const updateCampaignCellName = useCallback(
    (cellId: string, newName: string) => {
      setCampaignCells(
        campaignCells.map(cell => (cell.id === cellId ? { ...cell, name: newName } : cell))
      );
    },
    [campaignCells]
  );

  const deleteCampaignCell = useCallback(
    (cellId: string) => {
      if (campaignCells.length > 1) {
        const next = campaignCells.filter(cell => cell.id !== cellId);
        setCampaignCells(applyDefaultSplitsToCells(next));
      }
    },
    [campaignCells]
  );

  const toggleItemSelection = useCallback(
    (cellId: string, itemId: string, accountId: string, name: string) => {
      setCampaignCells(prevCells =>
        prevCells.map(cell => {
          if (cell.id !== cellId) {
            return cell;
          }

          const isSelected = cell.selectedItems.some(item => item.id === itemId);
          if (isSelected) {
            return {
              ...cell,
              selectedItems: cell.selectedItems.filter(item => item.id !== itemId),
            };
          } else {
            return {
              ...cell,
              selectedItems: [...cell.selectedItems, { id: itemId, accountId, name }],
            };
          }
        })
      );
    },
    []
  );

  const handleTestOptionsChange = useCallback(
    (newOptions: { accounts: boolean; campaigns: boolean; adSets: boolean }) => {
      const currentType = getActiveTestType(selectedTestOptions);
      const newType = getActiveTestType(newOptions);

      if (currentType !== newType) {
        setCampaignCells(prevCells => prevCells.map(cell => ({ ...cell, selectedItems: [] })));
      }

      setSelectedTestOptions(newOptions);
    },
    [selectedTestOptions]
  );

  const updateAllCampaignCellHoldoutPercentages = useCallback(
    (updatedCells: Array<{ id: string; testPercentage: number; controlPercentage: number }>) => {
      setCampaignCells(prevCells => {
        const mapped = prevCells.map(cell => {
          const updatedCell = updatedCells.find(uc => uc.id === cell.id);
          return updatedCell
            ? {
                ...cell,
                testPercentage: updatedCell.testPercentage,
                controlPercentage: updatedCell.controlPercentage,
              }
            : cell;
        });
        return applyRemainderCell(mapped, mapped.length - 1);
      });
    },
    []
  );

  const addObjectiveCell = useCallback(() => {
    const newObjectiveNumber = nextObjectiveId;
    const uniqueId = `${newObjectiveNumber}`;

    setNextObjectiveId(prev => prev + 1);
    setObjectiveCells([
      ...objectiveCells,
      {
        id: uniqueId,
        name: "",
        eventNames: [],
        customConversions: [],
      },
    ]);
  }, [objectiveCells, nextObjectiveId]);

  const updateObjectiveCellName = useCallback(
    (cellId: string, newName: string) => {
      setObjectiveCells(
        objectiveCells.map(cell => (cell.id === cellId ? { ...cell, name: newName } : cell))
      );
    },
    [objectiveCells]
  );

  const updateObjectiveCellSelections = useCallback(
    (cellId: string, eventNames: string[], customConversions: string[]) => {
      setObjectiveCells(prev =>
        prev.map(cell => (cell.id === cellId ? { ...cell, eventNames, customConversions } : cell))
      );
    },
    []
  );

  const deleteObjectiveCell = useCallback(
    (cellId: string) => {
      if (objectiveCells.length > 1) {
        setObjectiveCells(objectiveCells.filter(cell => cell.id !== cellId));
      }
    },
    [objectiveCells]
  );

  const isFormValid = useCallback((): boolean => {
    if (!dateInputs.start || !dateInputs.end) {
      return false;
    }

    const hasTestOption =
      selectedTestOptions.accounts || selectedTestOptions.campaigns || selectedTestOptions.adSets;
    if (!hasTestOption) {
      return false;
    }

    if (!campaignCells.some(cell => cell.selectedItems.length > 0)) {
      return false;
    }

    if (
      !objectiveCells.some(
        cell => (cell.eventNames?.length ?? 0) > 0 || (cell.customConversions?.length ?? 0) > 0
      )
    ) {
      return false;
    }

    return true;
  }, [dateInputs, selectedTestOptions, campaignCells, objectiveCells]);

  const areParamsDifferent = useCallback(
    (params1: TestPowerFormParams, params2: TestPowerFormParams): boolean => {
      if (
        params1.dateInputs.start !== params2.dateInputs.start ||
        params1.dateInputs.end !== params2.dateInputs.end
      ) {
        return true;
      }

      if (
        params1.selectedTestOptions.accounts !== params2.selectedTestOptions.accounts ||
        params1.selectedTestOptions.campaigns !== params2.selectedTestOptions.campaigns ||
        params1.selectedTestOptions.adSets !== params2.selectedTestOptions.adSets
      ) {
        return true;
      }

      if (params1.campaignCells.length !== params2.campaignCells.length) {
        return true;
      }
      for (let i = 0; i < params1.campaignCells.length; i++) {
        const cell1 = params1.campaignCells[i];
        const cell2 = params2.campaignCells[i];
        if (
          cell1.id !== cell2.id ||
          cell1.testPercentage !== cell2.testPercentage ||
          cell1.controlPercentage !== cell2.controlPercentage ||
          cell1.selectedItems.length !== cell2.selectedItems.length ||
          cell1.selectedItems.some((item, j) => item.id !== cell2.selectedItems[j]?.id)
        ) {
          return true;
        }
      }

      if (params1.objectiveCells.length !== params2.objectiveCells.length) {
        return true;
      }
      for (let i = 0; i < params1.objectiveCells.length; i++) {
        const obj1 = params1.objectiveCells[i];
        const obj2 = params2.objectiveCells[i];
        if (
          obj1.id !== obj2.id ||
          obj1.eventNames?.join() !== obj2.eventNames?.join() ||
          obj1.customConversions?.join() !== obj2.customConversions?.join()
        ) {
          return true;
        }
      }

      return false;
    },
    []
  );

  const checkTestPower = useCallback(async (): Promise<PowerCheckOutcome> => {
    if (!lockedAccountId || !startTimeUnix || !endTimeUnix) {
      return { status: "not_well_powered" };
    }

    setShowRunningModal(true);
    setPowerCheckError(null);

    try {
      const testEntityType = getActiveTestType(selectedTestOptions);

      const res = await MetaLambdaFetch("/getPowerTestResultsMultiCell", {
        params: {
          company,
          start_time: startTimeUnix,
          end_time: endTimeUnix,
          campaignCells: JSON.stringify(campaignCells),
          objectiveCells: JSON.stringify(objectiveCells),
          testEntityType,
          account_id: lockedAccountId,
        },
      });

      const results = await awaitJSON<MultiCellPowerTestResult>(res);

      setNeedDailySpend(results.totalNeededDailySpend);
      setHistoricalDailySpend(results.totalHistDailySpend);
      setTestLengthDays(results.testLengthDays);
      setPowerTestResult(results);
      setMinRequiredDurationDays(
        Math.ceil(
          (results.totalNeededDailySpend * results.testLengthDays) / results.totalHistDailySpend
        )
      );

      const cellResults = computeCellPowerResults(results);
      const isWellPowered = cellResults.length > 0 && cellResults.every(c => c.isPowered);

      const pairwiseValue = Object.values(results.feasibility?.pairwiseAnalysis ?? {})[0];

      const numericPairwiseValue = pairwiseValue
        ? Number(pairwiseValue.replace(/[^0-9.-]+/g, ""))
        : 0;

      setPairwiseValue(numericPairwiseValue);

      const isCellToCellSensitivityPowered =
        results.totalHistDailySpend * results.testLengthDays >= numericPairwiseValue;
      setIsCellToCellSensitivityPowered(isCellToCellSensitivityPowered);
      setShowRunningModal(false);

      return {
        status: isWellPowered && isCellToCellSensitivityPowered ? "well_powered" : "not_well_powered",
      };
    } catch (error) {
      setShowRunningModal(false);
      if (!(error instanceof FetchError) || error.status >= 500) {
        console.error("Power check failed:", error);
      }
      return { status: "error", message: getPowerCheckErrorMessage(error) };
    }
  }, [
    company,
    lockedAccountId,
    startTimeUnix,
    endTimeUnix,
    selectedTestOptions,
    campaignCells,
    objectiveCells,
  ]);

  const handleRunTestPowerCheck = useCallback(async () => {
    if (!isFormValid()) {
      return;
    }

    const formParams: TestPowerFormParams = {
      campaignCells,
      objectiveCells,
      dateInputs,
      selectedTestOptions,
    };

    const outcome = await checkTestPower();
    setTestPowerStatus(outcome.status);
    setPowerCheckError(outcome.status === "error" ? outcome.message : null);
    setLastCheckedParams(formParams);
  }, [campaignCells, objectiveCells, dateInputs, selectedTestOptions, checkTestPower, isFormValid]);

  const handleRunTestPowerCheckAsync = useCallback(async (): Promise<boolean> => {
    if (!isFormValid()) {
      return false;
    }

    const formParams: TestPowerFormParams = {
      campaignCells,
      objectiveCells,
      dateInputs,
      selectedTestOptions,
    };

    const outcome = await checkTestPower();
    setTestPowerStatus(outcome.status);
    setPowerCheckError(outcome.status === "error" ? outcome.message : null);
    setLastCheckedParams(formParams);

    return outcome.status === "well_powered";
  }, [campaignCells, objectiveCells, dateInputs, selectedTestOptions, checkTestPower, isFormValid]);

  const isResultsOutdated = useCallback((): boolean => {
    if (!lastCheckedParams || testPowerStatus === "not_run" || testPowerStatus === "error") {
      return false;
    }

    const currentParams: TestPowerFormParams = {
      campaignCells,
      objectiveCells,
      dateInputs,
      selectedTestOptions,
    };

    return areParamsDifferent(currentParams, lastCheckedParams);
  }, [
    lastCheckedParams,
    testPowerStatus,
    campaignCells,
    objectiveCells,
    dateInputs,
    selectedTestOptions,
    areParamsDifferent,
  ]);

  const handleAccountChange = useCallback((next: AllowedMetaAccount) => {
    setSelectedAccount(prev => {
      if (prev?.account_id === next.account_id) {
        return prev;
      }
      return next;
    });
    setCampaignCells(prev => prev.map(cell => ({ ...cell, selectedItems: [] })));
    setObjectiveCells(prev =>
      prev.map(cell => ({ ...cell, eventNames: [], customConversions: [] }))
    );
    setPowerTestResult(null);
    setTestPowerStatus("not_run");
    setLastCheckedParams(null);
  }, []);

  useEffect(() => {
    if (!company) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await MetaLambdaFetch("/getAllowedMetaAccounts", { params: { company } });
        const rows = await awaitJSON<AllowedMetaAccount[]>(res);
        if (cancelled) {
          return;
        }
        accountCacheRef.current.clear();
        setAccounts(rows);
        setSelectedAccount(rows[0]);
      } catch (e) {
        if (!cancelled) {
          setError({
            message: "Failed to load Meta accounts",
            reportError: e as Error,
          });
        }
      } finally {
        if (!cancelled) {
          setAccountsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, setError]);

  useEffect(() => {
    if (!company) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await MetaLambdaFetch("/getCustomEvents", { params: { company } });
        const events = await awaitJSON<CustomEventOption[]>(res);
        if (!cancelled) {
          setCustomEvents(events);
        }
      } catch (e) {
        if (!cancelled) {
          setError({
            message: "Failed to load Meta custom events",
            reportError: e as Error,
          });
        }
      } finally {
        if (!cancelled) {
          setCustomEventsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, setError]);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }
    const accountId = selectedAccount.account_id;
    const accountName = selectedAccount.account_name;

    const cached = accountCacheRef.current.get(accountId);
    if (cached) {
      setCampaignOptions(cached.campaigns);
      setPixelId(cached.pixelId);
      setCustomConversions(cached.customConversions);
      setCampaignsLoaded(true);
      setAccountDataLoaded(true);
      return;
    }

    setCampaignsLoaded(false);
    setAccountDataLoaded(false);

    let cancelled = false;
    (async () => {
      try {
        const res = await MetaLambdaFetch("/getMetaAccountData", {
          params: { company, account_id: accountId },
        });
        const accountData = await awaitJSON<AccountScopedData>(res);

        if (cancelled) {
          return;
        }

        accountCacheRef.current.set(accountId, accountData);
        setCampaignOptions(accountData.campaigns);
        setPixelId(accountData.pixelId);
        setCustomConversions(accountData.customConversions);
      } catch (e) {
        if (!cancelled) {
          setError({
            message: `Failed to load Meta data for ${accountName}`,
            reportError: e as Error,
          });
        }
      } finally {
        if (!cancelled) {
          setCampaignsLoaded(true);
          setAccountDataLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAccount, company, setError]);

  const handleCreateTest = useCallback(async (): Promise<string | null> => {
    if (!lockedAccountId) {
      throw new Error("Please select at least one campaign/ad set to test");
    }
    if (!pixelId) {
      throw new Error("Pixel ID not loaded. Please wait a moment and try again.");
    }
    if (!startTimeUnix || !endTimeUnix) {
      throw new Error("Please select valid start and end dates");
    }

    const testType = getActiveTestType(selectedTestOptions);
    const hasPowerCalcResults = powerTestResult !== null;
    const totalMinimumRequiredSpend = needDailySpend * testLengthDays;
    const totalExpectedSpend = historicalDailySpend * testLengthDays;
    const includePairwiseComparison = cellPowerResults.length > 1;
    const isCellsPowered = cellPowerResults.length > 0 && cellPowerResults.every(c => c.isPowered);
    const powered = hasPowerCalcResults && isCellsPowered && isCellToCellSensitivityPowered;
    const totalPowerCalcResults = hasPowerCalcResults
      ? buildTotalPowerCalcResults(totalMinimumRequiredSpend, totalExpectedSpend)
      : null;
    const cellLevelPowerCalcResults = hasPowerCalcResults
      ? buildCellLevelPowerCalcResults(
          cellPowerResults,
          pairwiseValue,
          totalExpectedSpend,
          includePairwiseComparison
        )
      : null;

    const payload = buildLiftTestPayload({
      company,
      userEmail,
      budget: Number(totalBudget.replace(/[^0-9]/g, "")),
      accountId: lockedAccountId,
      testName,
      testDescription,
      startTime: startTimeUnix,
      endTime: endTimeUnix,
      testType,
      campaignCells,
      objectiveCells,
      pixelId,
      feasibility: false,
      powered,
      totalPowerCalcResults,
      cellLevelPowerCalcResults,
    });

    const response = await MetaLambdaFetch("/createMetaLiftTest", {
      method: "POST",
      body: payload,
    });

    const result = await awaitJSON<{ study_id: string; message: string }>(response);
    return result.study_id;
  }, [
    company,
    userEmail,
    totalBudget,
    lockedAccountId,
    pixelId,
    startTimeUnix,
    endTimeUnix,
    selectedTestOptions,
    testName,
    testDescription,
    campaignCells,
    objectiveCells,
    needDailySpend,
    historicalDailySpend,
    testLengthDays,
    powerTestResult,
    cellPowerResults,
    pairwiseValue,
    isCellToCellSensitivityPowered,
  ]);

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (accounts.length === 0) {
    return (
      <div className="metaLiftEmptyAccounts">
        No Meta ad accounts are configured for this company.
      </div>
    );
  }

  return (
    <>
      <div className="metaLiftAccountSelectorBar">
        {accounts.length === 1 ? (
          <div className="metaLiftSelectedAccount">
            Ad Account: <strong>{selectedAccount?.account_name}</strong>
          </div>
        ) : (
          <Dropdown
            type={DropdownToggleType.OUTLINED}
            background="light"
            size="lg"
            label="Ad Account"
            value={selectedAccount?.account_id}
            options={accounts.map(a => ({ value: a.account_id, label: a.account_name }))}
            onChange={(account_id: string) => {
              const next = accounts.find(a => a.account_id === account_id);
              if (next) {
                handleAccountChange(next);
              }
            }}
          />
        )}
      </div>
      <div className="metaLiftWidgetWrapper">
        <RunningPowerCheckModal show={showRunningModal} onHide={() => {}} />
        <MetaLiftTestPower
          status={testPowerStatus}
          errorMessage={powerCheckError}
          onRunTest={handleRunTestPowerCheck}
          isFormValid={isFormValid()}
          isResultsOutdated={isResultsOutdated()}
          isCellToCellSensitivityPowered={isCellToCellSensitivityPowered}
          neededDailySpend={needDailySpend}
          historicalDailySpend={historicalDailySpend}
          testLengthDays={testLengthDays}
          minRequiredDurationDays={minRequiredDurationDays}
          cellPowerResults={cellPowerResults}
          campaignCells={campaignCells}
          pairwiseValue={pairwiseValue}
        />
        <MetaLiftForm
          testName={testName}
          setTestName={setTestName}
          testDescription={testDescription}
          setTestDescription={setTestDescription}
          testHypothesis={testHypothesis}
          setTestHypothesis={setTestHypothesis}
          plannedActions={plannedActions}
          setPlannedActions={setPlannedActions}
          dateInputs={dateInputs}
          setDateInputs={setDateInputs}
          timeZone={timeZone}
          setTimeZone={setTimeZone}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          selectedTestOptions={selectedTestOptions}
          setSelectedTestOptions={handleTestOptionsChange}
          campaignCells={campaignCells}
          addCampaignCell={addCampaignCell}
          updateCampaignCellName={updateCampaignCellName}
          deleteCampaignCell={deleteCampaignCell}
          updateAllCampaignCellHoldoutPercentages={updateAllCampaignCellHoldoutPercentages}
          toggleItemSelection={toggleItemSelection}
          campaignOptions={campaignOptions || []}
          lockedAccountId={lockedAccountId}
          allSelectedItemIds={allSelectedItemIds}
          totalBudget={totalBudget}
          objectiveCells={objectiveCells}
          addObjectiveCell={addObjectiveCell}
          updateObjectiveCellName={updateObjectiveCellName}
          updateObjectiveCellSelections={updateObjectiveCellSelections}
          deleteObjectiveCell={deleteObjectiveCell}
          customConversions={customConversions}
          customEvents={customEvents}
          testPowerStatus={testPowerStatus}
          neededDailySpend={needDailySpend}
          historicalDailySpend={historicalDailySpend}
          testLengthDays={testLengthDays}
          cellPowerResults={cellPowerResults}
          onRunTestPowerCheck={handleRunTestPowerCheckAsync}
          onCreateTest={handleCreateTest}
          isFormValid={isFormValid()}
          isResultsOutdated={isResultsOutdated()}
          onNavigateToPublished={onNavigateToPublished}
          isCellToCellSensitivityPowered={isCellToCellSensitivityPowered}
        />
        <MetaLiftBestPractices />
      </div>
    </>
  );
};

export default MetaLiftCreate;
